import React, { useState, useEffect, useRef } from 'react';
import { X, ExternalLink, Bookmark, ChevronDown, ChevronUp, Award, Calendar, Building2, GraduationCap, RefreshCw, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/formatters';
import { normalizeEligibility, deriveEligibilityBullets } from '../../utils/eligibilityNormalizer';

/**
 * Authority Preview Panel - Enterprise-grade scholarship/grant preview
 * Replaces legacy modal with authoritative, decision-focused panel
 */
const AuthorityPreviewPanel = ({ opportunity, isOpen, onClose, onSave, isSaved, appliedTimestamp, onApplied }) => {
  const { user, isAuthenticated } = useAuth();
  const [showFullEligibility, setShowFullEligibility] = useState(false);
  const [redirectNotice, setRedirectNotice] = useState(false);
  const [justApplied, setJustApplied] = useState(false);
  const [applyTimestamp, setApplyTimestamp] = useState(appliedTimestamp);
  
  // Ref for auto-scrolling on mobile when expanding details
  const detailsRef = useRef(null);
  
  // Handle toggle for "Full Details" with mobile auto-scroll
  const handleToggleDetails = () => {
    const willExpand = !showFullEligibility;
    setShowFullEligibility(willExpand);
    
    // On mobile, auto-scroll to reveal expanded content
    if (willExpand && window.innerWidth < 768) {
      // Small delay to allow DOM to render expanded content
      setTimeout(() => {
        if (detailsRef.current) {
          detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  // Update applyTimestamp when prop changes
  useEffect(() => {
    setApplyTimestamp(appliedTimestamp);
  }, [appliedTimestamp]);

  // Reset state when panel opens
  useEffect(() => {
    if (isOpen) {
      // Reset expand/redirect states when panel opens
      setShowFullEligibility(false);
      setRedirectNotice(false);
      setJustApplied(false);
    }
  }, [isOpen]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !opportunity) return null;

  const hasApplied = !!applyTimestamp || justApplied;

  // Use imported deriveEligibilityBullets from normalizer utility
  const eligibilityBullets = deriveEligibilityBullets(opportunity.description);

  // Get status badge styling
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return { bg: 'bg-green-100', text: 'text-green-800', label: 'Open' };
      case 'closing_soon':
        return { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Closing Soon' };
      case 'rolling':
        return { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Rolling' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Check Deadline' };
    }
  };

  const statusBadge = getStatusBadge(opportunity.deadlineStatus);

  // Log outbound intent and open apply URL
  const handleApplyClick = async () => {
    const applyUrl = opportunity.applyUrl || opportunity.infoUrl;
    
    if (!applyUrl) {
      console.warn('No apply URL available for this opportunity');
      return;
    }

    const timestamp = new Date().toISOString();

    // Log outbound intent event
    try {
      const eventData = {
        funding_id: opportunity.id,
        funding_type: opportunity.fundingType || 'scholarship',
        user_id: isAuthenticated ? user?.id : null,
        session_id: sessionStorage.getItem('session_id') || 'anonymous',
        timestamp: timestamp,
        action: 'apply_click'
      };

      // Fire and forget - don't block on this
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/funding/outbound-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventData)
      }).then(() => {
        // Update local state after successful log
        setJustApplied(true);
        setApplyTimestamp(timestamp);
        // Notify parent component
        if (onApplied) {
          onApplied(opportunity.id, timestamp);
        }
      }).catch(err => console.log('Intent logging failed (non-blocking):', err));

    } catch (err) {
      // Non-blocking - continue with redirect
      console.log('Intent logging error (non-blocking):', err);
    }

    // Show redirect notice
    setRedirectNotice(true);

    // Open in new tab immediately
    window.open(applyUrl, '_blank', 'noopener,noreferrer');
  };

  // Format applied date
  const formatAppliedDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format level of study array
  const formatLevelOfStudy = (levels) => {
    if (!levels || !Array.isArray(levels) || levels.length === 0) return null;
    
    const labelMap = {
      'high_school': 'High School',
      'undergraduate': 'Undergraduate',
      'graduate': 'Graduate'
    };
    
    const unique = [...new Set(levels)];
    return unique.map(l => labelMap[l] || l).join(' • ');
  };

  // Format duration
  const formatDuration = (duration) => {
    if (!duration) return null;
    const map = {
      'one_time': 'One-Time Award',
      'annual': 'Annual Award',
      'multi_year': 'Multi-Year Award',
      'rolling': 'Rolling Award',
      'unknown': null
    };
    return map[duration] || duration;
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9998] transition-opacity"
        onClick={onClose}
      />

      {/* Panel - Desktop: centered, Mobile: bottom sheet */}
      <div className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center pointer-events-none">
        <div 
          className="pointer-events-auto bg-white w-full md:w-[640px] md:max-w-[90vw] md:rounded-xl md:max-h-[85vh] max-h-[90vh] flex flex-col shadow-2xl rounded-t-2xl md:rounded-2xl overflow-hidden transform transition-transform"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile drag handle */}
          <div className="md:hidden flex justify-center py-2 bg-white">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
          </div>

          {/* === 1. HEADER (Authority Zone) === */}
          <div className="bg-gradient-to-br from-[#1a5d3a] to-[#2d7a4d] text-white p-5 md:p-6">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 md:top-4 md:right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close panel"
            >
              <X size={20} />
            </button>

            {/* Type badge + Status */}
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/20 rounded-full text-xs font-semibold uppercase tracking-wide">
                <Award size={14} />
                {opportunity.fundingType || 'Scholarship'}
              </span>
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                <Clock size={12} />
                {statusBadge.label}
              </span>
            </div>

            {/* Title */}
            <h2 className="text-xl md:text-2xl font-bold mb-3 pr-8 leading-tight">
              {opportunity.name}
            </h2>

            {/* Award Amount - Primary Anchor */}
            <div className="text-3xl md:text-4xl font-bold text-[#f5a623] mb-2">
              {opportunity.amount || 'Amount Varies'}
            </div>

            {/* Deadline */}
            <div className="flex items-center gap-2 text-white/90">
              <Calendar size={16} />
              <span className="font-medium">
                Deadline: {opportunity.deadline ? formatDate(opportunity.deadline) : opportunity.deadlineRaw || 'See official site'}
              </span>
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto">
            {/* === 2. SPONSOR & SCOPE === */}
            <div className="px-5 md:px-6 py-4 border-b border-gray-100">
              {/* Sponsor */}
              {opportunity.sponsor && (
                <div className="flex items-start gap-3 mb-3">
                  <Building2 size={18} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Sponsor</div>
                    <div className="font-medium text-gray-900">{opportunity.sponsor}</div>
                  </div>
                </div>
              )}

              {/* Level of Study */}
              {formatLevelOfStudy(opportunity.levelOfStudy) && (
                <div className="flex items-start gap-3 mb-3">
                  <GraduationCap size={18} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Level of Study</div>
                    <div className="font-medium text-gray-900">{formatLevelOfStudy(opportunity.levelOfStudy)}</div>
                  </div>
                </div>
              )}

              {/* Renewable Indicator */}
              {opportunity.renewable && (
                <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-green-50 rounded-lg">
                  <RefreshCw size={16} className="text-green-600" />
                  <span className="text-sm font-medium text-green-800">Renewable Award</span>
                </div>
              )}
            </div>

            {/* === 3. ELIGIBILITY SNAPSHOT === */}
            <div className="px-5 md:px-6 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                Eligibility Snapshot
              </h3>
              
              {eligibilityBullets ? (
                <ul className="space-y-2">
                  {eligibilityBullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-[#1a5d3a] rounded-full mt-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{bullet}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-600 italic">
                  See full eligibility details below
                </p>
              )}
            </div>

            {/* === 4. DETAILS (Expandable) === */}
            <div className="px-5 md:px-6 py-4" ref={detailsRef}>
              <button
                onClick={handleToggleDetails}
                className="flex items-center justify-between w-full text-left"
              >
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                  Full Details
                </h3>
                {showFullEligibility ? (
                  <ChevronUp size={20} className="text-gray-400" />
                ) : (
                  <ChevronDown size={20} className="text-gray-400" />
                )}
              </button>

              {showFullEligibility && (
                <div className="mt-4 space-y-4">
                  {/* Full Eligibility Text */}
                  {opportunity.description && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Eligibility Requirements</div>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {normalizeEligibility(opportunity.description)}
                      </p>
                    </div>
                  )}

                  {/* Number of Awards */}
                  {opportunity.numberOfAwards && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Number of Awards</div>
                      <p className="text-sm text-gray-900 font-medium">{opportunity.numberOfAwards}</p>
                    </div>
                  )}

                  {/* Duration */}
                  {formatDuration(opportunity.type?.toLowerCase().replace(' ', '_')) && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Award Duration</div>
                      <p className="text-sm text-gray-900 font-medium">
                        {formatDuration(opportunity.type?.toLowerCase().replace(' ', '_'))}
                      </p>
                    </div>
                  )}

                  {/* Source ID for transparency */}
                  {opportunity.sourceId && (
                    <div className="pt-2 border-t border-gray-100">
                      <div className="text-xs text-gray-400">
                        Reference ID: {opportunity.sourceId}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* === 5. ACTION ZONE (Fixed Footer) === */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 md:px-6 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
            {/* Visited Status Notice */}
            {hasApplied && (
              <div className="mb-3 px-3 py-2 bg-green-50 rounded-lg text-center">
                <p className="text-sm text-green-800 flex items-center justify-center gap-2">
                  <ExternalLink size={16} className="text-green-600" />
                  You visited the official site on {formatAppliedDate(applyTimestamp)}
                </p>
              </div>
            )}

            {/* Redirect Notice */}
            {redirectNotice && !hasApplied && (
              <div className="mb-3 px-3 py-2 bg-blue-50 rounded-lg text-center">
                <p className="text-sm text-blue-800">
                  <ExternalLink size={14} className="inline mr-1" />
                  You&apos;re being redirected to the official application site.
                </p>
              </div>
            )}

            <div className="flex items-center gap-3">
              {/* Save Button */}
              <button
                onClick={() => onSave && onSave(opportunity.id)}
                className={`p-3 rounded-lg border transition-colors ${
                  isSaved 
                    ? 'bg-[#f5a623]/10 border-[#f5a623] text-[#f5a623]' 
                    : 'border-gray-300 text-gray-500 hover:border-[#f5a623] hover:text-[#f5a623]'
                }`}
                aria-label={isSaved ? 'Saved' : 'Save for later'}
                title={isSaved ? 'Saved' : 'Save for later'}
              >
                <Bookmark size={20} fill={isSaved ? '#f5a623' : 'none'} />
              </button>

              {/* Primary CTA - Shows Visited state or Apply button */}
              {hasApplied ? (
                <div className="flex-1 flex flex-col gap-2">
                  <button
                    disabled
                    className="flex items-center justify-center gap-2 bg-green-100 text-green-800 font-semibold py-3.5 px-6 rounded-lg cursor-default"
                  >
                    <ExternalLink size={18} />
                    <span>Visited Official Site</span>
                  </button>
                  <button
                    onClick={handleApplyClick}
                    className="text-sm text-[#1a5d3a] hover:text-[#2d7a4d] underline flex items-center justify-center gap-1"
                  >
                    <span>Visit site again</span>
                    <ExternalLink size={14} className="md:hidden" />
                  </button>
                </div>
              ) : (
                <div className="flex-1 flex flex-col">
                  <button
                    onClick={handleApplyClick}
                    disabled={!opportunity.applyUrl && !opportunity.infoUrl}
                    className="flex items-center justify-center gap-2 bg-[#f5a623] hover:bg-[#e09000] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3.5 px-6 rounded-lg transition-colors"
                  >
                    <span>Apply on Official Site</span>
                    <ExternalLink size={18} />
                  </button>
                  {/* Mobile-only hint for external link */}
                  <span className="md:hidden text-xs text-gray-500 text-center mt-1.5">
                    Opens in new tab
                  </span>
                </div>
              )}
            </div>

            {/* No URL warning */}
            {!hasApplied && !opportunity.applyUrl && !opportunity.infoUrl && (
              <p className="mt-2 text-xs text-gray-500 text-center">
                Application URL not available. Contact the sponsor directly.
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthorityPreviewPanel;
